/**
 * Custom Evaluator Detail / Edit Dialog.
 *
 * Surfaces the full state of a custom evaluator set and lets the user:
 *   - inspect any version (active / draft / archived) in read-only mode,
 *   - edit a draft version's criteria in place (PUT /versions/{id}),
 *   - publish a draft to make it active,
 *   - delete a draft, or
 *   - delete the entire evaluator set.
 *
 * Editing an active (published) version is not allowed by the backend; instead
 * we offer a one-click "Edit (creates new draft)" path that calls
 * POST /versions with `copy_from_active: true` and switches the selection to
 * the new draft.
 *
 * Set-level `name` and `description` are immutable on the backend — they are
 * surfaced as read-only here.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Loader2, Trash2 } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/hooks/use-toast';
import {
  useCreateCustomEvalVersion,
  useCustomEvalSet,
  useDeleteCustomEvalSet,
  useDeleteCustomEvalVersion,
  usePublishCustomEvalVersion,
  useUpdateCustomEvalVersion,
} from '@/hooks/useCustomEvals';
import type {
  CustomEvalCriterionResponse,
  CustomEvalSetResponse,
  CustomEvalSetVersionResponse,
} from '@/lib/api/customEvals';
import { VersionBody } from './CustomEvalVersionBody';
import {
  MAX_CRITERIA,
  NAME_MAX,
  type EditableCriterion,
  type EditState,
} from './CustomEvalDetailDialog.model';

// ─── Limits (mirror backend schemas.py) ─────────────────────────────────────

const KEY_MAX = 128;

// ─── Helpers ────────────────────────────────────────────────────────────────

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, KEY_MAX);
}

// Matches every key shape `addCriterion` produces for newly-added (unpersisted)
// criteria: `new-criterion-N`, `criterion-N` (slugify-empty fallback), and the
// suffix-loop variant `new-criterion-N-M`. AI-builder slugs and manually-typed
// keys do not match, so they pass through untouched at save time.
const TENTATIVE_KEY_RE = /^(new-)?criterion-\d+(-\d+)?$/;

function isTentativeKey(key: string): boolean {
  return TENTATIVE_KEY_RE.test(key);
}

function toEditableCriterion(c: CustomEvalCriterionResponse): EditableCriterion {
  return {
    key: c.key,
    name: c.name,
    rubric: c.rubric,
    score_type: c.score_type,
    weight: c.weight,
    order_index: c.order_index,
    is_required: c.is_required,
    pass_threshold: c.pass_threshold,
    metadata: (c.metadata ?? null) as Record<string, unknown> | null,
  };
}

function toEditState(version: CustomEvalSetVersionResponse | null): EditState {
  if (!version) return { notes: '', criteria: [] };
  const sorted = [...(version.criteria ?? [])].sort(
    (a, b) => a.order_index - b.order_index,
  );
  return {
    notes: version.notes ?? '',
    criteria: sorted.map(toEditableCriterion),
  };
}

/** Stable serialization for dirty-check; structure-only, ignores object identity. */
function snapshot(state: EditState): string {
  return JSON.stringify({
    notes: state.notes,
    criteria: state.criteria.map((c) => ({
      key: c.key,
      name: c.name,
      rubric: c.rubric,
      score_type: c.score_type,
      weight: c.weight,
      is_required: c.is_required,
      pass_threshold: c.pass_threshold,
      metadata: c.metadata,
    })),
  });
}

function statusLabel(status: string, versionNumber: number): string {
  if (status === 'published') return `Active v${versionNumber}`;
  if (status === 'draft') return `Draft v${versionNumber}`;
  if (status === 'archived') return `Archived v${versionNumber}`;
  return `v${versionNumber}`;
}

function statusVariant(status: string): 'default' | 'secondary' | 'outline' {
  if (status === 'published') return 'default';
  if (status === 'draft') return 'secondary';
  return 'outline';
}

/** Order versions for the tab strip: active first, then drafts (newest first), then archived. */
function orderVersions(versions: CustomEvalSetVersionResponse[]): CustomEvalSetVersionResponse[] {
  const drafts = versions
    .filter((v) => v.status === 'draft')
    .sort((a, b) => b.version_number - a.version_number);
  const archived = versions
    .filter((v) => v.status === 'archived')
    .sort((a, b) => b.version_number - a.version_number);
  const active = versions.find((v) => v.status === 'published') ?? null;

  return [...(active ? [active] : []), ...drafts, ...archived];
}

function defaultSelectedVersionId(set: CustomEvalSetResponse | undefined): string | null {
  if (!set) return null;
  const versions = set.versions ?? [];
  const active = versions.find((v) => v.id === set.active_version_id) ?? null;
  if (active) return active.id;
  const draft = [...versions]
    .filter((v) => v.status === 'draft')
    .sort((a, b) => b.version_number - a.version_number)[0];
  if (draft) return draft.id;
  const archived = [...versions]
    .filter((v) => v.status === 'archived')
    .sort((a, b) => b.version_number - a.version_number)[0];
  return archived?.id ?? null;
}

// ─── Component ──────────────────────────────────────────────────────────────

interface CustomEvalDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  setId: string | null;
  /**
   * Name of another set in the same org currently holding active status, if any.
   * Used to surface the cross-set demotion that activation will trigger.
   */
  otherActiveSetName?: string | null;
}

export function CustomEvalDetailDialog({
  open,
  onOpenChange,
  organizationId,
  setId,
  otherActiveSetName = null,
}: CustomEvalDetailDialogProps) {
  const {
    data: set,
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
  } = useCustomEvalSet(organizationId, setId, { enabled: open });

  const versionsOrdered = useMemo(
    () => (set ? orderVersions(set.versions ?? []) : []),
    [set],
  );

  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [editState, setEditState] = useState<EditState>({ notes: '', criteria: [] });
  const [pristine, setPristine] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [confirmDeleteDraft, setConfirmDeleteDraft] = useState(false);
  const [confirmDeleteSet, setConfirmDeleteSet] = useState(false);
  const [confirmEditFromActive, setConfirmEditFromActive] = useState(false);
  const [confirmActivate, setConfirmActivate] = useState(false);

  const updateMutation = useUpdateCustomEvalVersion();
  const publishMutation = usePublishCustomEvalVersion();
  const createVersionMutation = useCreateCustomEvalVersion();
  const deleteVersionMutation = useDeleteCustomEvalVersion();
  const deleteSetMutation = useDeleteCustomEvalSet();

  const isMutating =
    updateMutation.isPending ||
    publishMutation.isPending ||
    createVersionMutation.isPending ||
    deleteVersionMutation.isPending ||
    deleteSetMutation.isPending;

  // Initialize / re-sync selected version + editor when the dialog opens or
  // the underlying data changes. We re-anchor whenever the version's
  // updated_at moves (covers post-save refetch and concurrent updates).
  const selectedVersion = useMemo(
    () => versionsOrdered.find((v) => v.id === selectedVersionId) ?? null,
    [versionsOrdered, selectedVersionId],
  );
  const versionFingerprint = selectedVersion
    ? `${selectedVersion.id}:${selectedVersion.updated_at}:${selectedVersion.status}`
    : '';

  // Reset selection on open / setId change.
  useEffect(() => {
    if (!open) return;
    setErrorMessage(null);
    setConfirmDiscard(false);
    setConfirmDeleteDraft(false);
    setConfirmDeleteSet(false);
    setConfirmEditFromActive(false);
    setConfirmActivate(false);
    updateMutation.reset();
    publishMutation.reset();
    createVersionMutation.reset();
    deleteVersionMutation.reset();
    deleteSetMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, setId]);

  // When the set first loads (or its active version changes), pick a default
  // version to display.
  useEffect(() => {
    if (!set) return;
    setSelectedVersionId((current) => {
      if (current && versionsOrdered.some((v) => v.id === current)) return current;
      return defaultSelectedVersionId(set);
    });
  }, [set, versionsOrdered]);

  // Sync editor state to the selected version whenever it loads / updates.
  useEffect(() => {
    if (!selectedVersion) {
      setEditState({ notes: '', criteria: [] });
      setPristine(snapshot({ notes: '', criteria: [] }));
      return;
    }
    const next = toEditState(selectedVersion);
    setEditState(next);
    setPristine(snapshot(next));
  }, [versionFingerprint, selectedVersion]);

  const isDraft = selectedVersion?.status === 'draft';
  const isPublished = selectedVersion?.status === 'published';
  const isArchived = selectedVersion?.status === 'archived';
  const dirty = isDraft && snapshot(editState) !== pristine;

  // Find a sibling draft when viewing the active version, so we can offer to
  // open it instead of creating yet another draft.
  const existingDraft = useMemo(() => {
    if (!set) return null;
    const drafts = (set.versions ?? [])
      .filter((v) => v.status === 'draft')
      .sort((a, b) => b.version_number - a.version_number);
    return drafts[0] ?? null;
  }, [set]);

  // Currently-active version *within this set*, used to surface the in-set
  // version-replacement consequence in the activate confirm.
  const inSetActiveVersion = useMemo(() => {
    if (!set?.active_version_id) return null;
    const versions = set.versions ?? [];
    const match =
      set.active_version ??
      versions.find((v) => v.id === set.active_version_id) ??
      null;
    return match;
  }, [set]);

  // ─── Close handling ──────────────────────────────────────────────────────

  const handleOpenChange = (next: boolean) => {
    if (next === open) return;
    if (!next && isMutating) return; // block close while a mutation is in flight
    if (!next && dirty) {
      setConfirmDiscard(true);
      return;
    }
    onOpenChange(next);
  };

  const handleDiscardConfirm = () => {
    setConfirmDiscard(false);
    onOpenChange(false);
  };

  // ─── Validation ──────────────────────────────────────────────────────────

  const validate = (): string | null => {
    if (editState.criteria.length < 1) return 'At least one criterion is required.';
    if (editState.criteria.length > MAX_CRITERIA) {
      return `A version may have at most ${MAX_CRITERIA} criteria.`;
    }
    const seenKeys = new Set<string>();
    for (let i = 0; i < editState.criteria.length; i += 1) {
      const c = editState.criteria[i];
      const trimmedName = c.name.trim();
      if (!trimmedName) return `Criterion ${i + 1}: name is required.`;
      if (trimmedName.length > NAME_MAX) {
        return `Criterion ${i + 1}: name must be ${NAME_MAX} characters or fewer.`;
      }
      if (!c.rubric.trim()) return `Criterion ${i + 1}: scoring instructions are required.`;
      if (!c.key) return `Criterion ${i + 1}: missing key.`;
      if (seenKeys.has(c.key)) {
        return `Criterion ${i + 1}: duplicate key "${c.key}". Rename so the slug differs.`;
      }
      seenKeys.add(c.key);
    }
    return null;
  };

  // ─── Mutation flows ──────────────────────────────────────────────────────

  const buildUpdatePayload = () => {
    if (!selectedVersion) return null;
    // For criteria still carrying placeholder keys (added via `+` and not yet
    // persisted), slugify the user-entered name into a meaningful key at
    // submit time. Keeping the substitution at submit-time keeps Tabs / React
    // identity stable while the user types — the editor key is `c.key` until
    // the server response refreshes the editor state.
    const submittedKeys = new Set<string>();
    return {
      notes: editState.notes,
      model_name: selectedVersion.model_name,
      temperature: selectedVersion.temperature,
      criteria: editState.criteria.map((c, idx) => {
        const trimmedName = c.name.trim();
        let resolvedKey = c.key;
        if (isTentativeKey(c.key) && trimmedName.length > 0) {
          const fromName = slugify(trimmedName);
          if (fromName.length > 0) {
            let candidate = fromName;
            let suffix = 2;
            while (submittedKeys.has(candidate)) {
              candidate = `${fromName}-${suffix}`;
              suffix += 1;
            }
            resolvedKey = candidate;
          }
        }
        submittedKeys.add(resolvedKey);
        return {
          key: resolvedKey,
          name: trimmedName,
          rubric: c.rubric.trim(),
          score_type: c.score_type,
          pass_threshold: c.pass_threshold,
          weight: c.weight,
          order_index: idx,
          is_required: c.is_required,
          // Preserve metadata exactly as-is. We only display it; we don't edit it.
          metadata: c.metadata ?? undefined,
        };
      }),
    };
  };

  const mapErrorAndMaybeClose = async (err: unknown, fallback: string) => {
    const message = err instanceof Error ? err.message : fallback;
    const lower = message.toLowerCase();
    // 404 — the set or version disappeared from under us.
    if (lower.includes('not found') || lower.includes('404')) {
      toast({
        title: 'Evaluator no longer exists',
        description: 'It may have been deleted in another session.',
        variant: 'destructive',
      });
      onOpenChange(false);
      return;
    }
    // 409 — backend rejected because version is no longer a draft, or some
    // other conflict. Refetch so the UI reflects current state.
    if (lower.includes('only draft') || lower.includes('conflict') || lower.includes('409')) {
      toast({
        title: 'This version can no longer be edited',
        description: message,
        variant: 'destructive',
      });
      await refetch();
      return;
    }
    setErrorMessage(message);
    toast({
      title: fallback,
      description: message,
      variant: 'destructive',
    });
  };

  const handleSaveDraft = async (alsoActivate: boolean) => {
    if (!set || !selectedVersion || !isDraft) return;
    const validationError = validate();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }
    setErrorMessage(null);
    const payload = buildUpdatePayload();
    if (!payload) return;

    try {
      const updated = await updateMutation.mutateAsync({
        setId: set.id,
        versionId: selectedVersion.id,
        organizationId,
        data: payload,
      });

      if (alsoActivate) {
        try {
          await publishMutation.mutateAsync({
            setId: set.id,
            versionId: updated.id,
            organizationId,
          });
          toast({
            title: 'Evaluator activated',
            description: `"${set.name}" is now active.`,
          });
          onOpenChange(false);
        } catch (publishErr) {
          // Update succeeded; surface the publish-specific failure but keep
          // the dialog open so the user can retry from a saved-draft state.
          const message =
            publishErr instanceof Error ? publishErr.message : 'Unknown error';
          toast({
            title: 'Saved as draft',
            description: `Failed to activate — try again from this dialog. (${message})`,
          });
        }
      } else {
        toast({ title: 'Draft updated' });
      }
    } catch (err) {
      await mapErrorAndMaybeClose(err, 'Could not save draft');
    }
  };

  const handleDeleteDraft = async () => {
    if (!set || !selectedVersion || !isDraft) return;
    setConfirmDeleteDraft(false);
    try {
      await deleteVersionMutation.mutateAsync({
        setId: set.id,
        versionId: selectedVersion.id,
        organizationId,
      });
      toast({ title: 'Draft deleted' });

      // After deletion, refetched data may or may not have other versions.
      // Reset selection so the next render picks a sensible default.
      setSelectedVersionId(null);

      // If this was the only version, the set still exists but is now empty —
      // closing is the cleanest UX.
      const remaining = (set.versions ?? []).filter((v) => v.id !== selectedVersion.id);
      if (remaining.length === 0) onOpenChange(false);
    } catch (err) {
      await mapErrorAndMaybeClose(err, 'Could not delete draft');
    }
  };

  const handleDeleteSet = async () => {
    if (!set) return;
    setConfirmDeleteSet(false);
    try {
      await deleteSetMutation.mutateAsync({ setId: set.id, organizationId });
      toast({
        title: 'Evaluator deleted',
        description: `"${set.name}" has been removed.`,
      });
      onOpenChange(false);
    } catch (err) {
      await mapErrorAndMaybeClose(err, 'Could not delete evaluator');
    }
  };

  const handleEditFromActive = async () => {
    if (!set || !selectedVersion || !isPublished) return;
    setConfirmEditFromActive(false);
    try {
      const newDraft = await createVersionMutation.mutateAsync({
        setId: set.id,
        organizationId,
        data: {
          criteria: [],
          copy_from_active: true,
        },
      });
      toast({
        title: 'Draft created',
        description: 'Edit the draft, then activate to replace the live version.',
      });
      setSelectedVersionId(newDraft.id);
    } catch (err) {
      await mapErrorAndMaybeClose(err, 'Could not create draft');
    }
  };

  const handleOpenExistingDraft = () => {
    if (!existingDraft) return;
    setSelectedVersionId(existingDraft.id);
  };

  // ─── Editor mutations ────────────────────────────────────────────────────

  const updateCriterion = (
    idx: number,
    patch: Partial<EditableCriterion>,
  ) => {
    setEditState((prev) => {
      const next = [...prev.criteria];
      const current = next[idx];
      if (!current) return prev;
      const merged = { ...current, ...patch };
      // Re-derive key from name when the name changes AND this criterion has
      // never been persisted (no metadata.generated_by set OR key looks
      // tentative). Keep the key stable for criteria coming from the server,
      // since the backend enforces uniqueness across the version.
      next[idx] = merged;
      return { ...prev, criteria: next };
    });
    setErrorMessage(null);
  };

  const addCriterion = () => {
    if (editState.criteria.length >= MAX_CRITERIA) return;
    setEditState((prev) => {
      const baseName = `New criterion ${prev.criteria.length + 1}`;
      let candidateKey = slugify(baseName) || `criterion-${prev.criteria.length + 1}`;
      const existingKeys = new Set(prev.criteria.map((c) => c.key));
      let suffix = 2;
      while (existingKeys.has(candidateKey)) {
        candidateKey = `${slugify(baseName)}-${suffix}`;
        suffix += 1;
      }
      const fresh: EditableCriterion = {
        key: candidateKey,
        name: baseName,
        rubric: '',
        score_type: 'integer_0_100',
        weight: 1.0,
        order_index: prev.criteria.length,
        is_required: true,
        pass_threshold: null,
        metadata: null,
      };
      return { ...prev, criteria: [...prev.criteria, fresh] };
    });
    setErrorMessage(null);
  };

  const removeCriterion = (idx: number) => {
    setEditState((prev) => {
      if (prev.criteria.length <= 1) return prev;
      const next = prev.criteria.filter((_, i) => i !== idx);
      return { ...prev, criteria: next };
    });
    setErrorMessage(null);
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{set?.name ?? 'Evaluator'}</DialogTitle>
            <DialogDescription>
              {set?.description?.trim() ||
                'Inspect or edit this evaluator. Active versions are immutable; edit a draft instead.'}
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : isError || !set ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="space-y-2">
                <p>{error instanceof Error ? error.message : 'Failed to load evaluator.'}</p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => refetch()}
                  disabled={isRefetching}
                >
                  {isRefetching ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Retrying&hellip;
                    </span>
                  ) : (
                    'Retry'
                  )}
                </Button>
              </AlertDescription>
            </Alert>
          ) : versionsOrdered.length === 0 ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                This evaluator has no versions. You may want to delete it.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-4">
              {/* Version selector */}
              {versionsOrdered.length > 1 ? (
                <Tabs
                  value={selectedVersionId ?? undefined}
                  onValueChange={(value) => {
                    if (dirty) {
                      // Switching tabs while dirty is a soft discard. Confirm.
                      const ok = window.confirm(
                        'Discard unsaved changes and switch versions?',
                      );
                      if (!ok) return;
                    }
                    setSelectedVersionId(value);
                  }}
                >
                  <TabsList className="flex flex-wrap h-auto">
                    {versionsOrdered.map((v) => (
                      <TabsTrigger key={v.id} value={v.id} className="text-xs">
                        {statusLabel(v.status, v.version_number)}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              ) : (
                selectedVersion && (
                  <Badge variant={statusVariant(selectedVersion.status)}>
                    {statusLabel(selectedVersion.status, selectedVersion.version_number)}
                  </Badge>
                )
              )}

              {/* Version body */}
              {selectedVersion && (
                <VersionBody
                  version={selectedVersion}
                  state={editState}
                  isDraft={!!isDraft}
                  onUpdateCriterion={updateCriterion}
                  onAddCriterion={addCriterion}
                  onRemoveCriterion={removeCriterion}
                  onUpdateNotes={(notes) =>
                    setEditState((prev) => ({ ...prev, notes }))
                  }
                  isBusy={isMutating}
                />
              )}

              {errorMessage && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              )}

              {/* Action footer */}
              {selectedVersion && (
                <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-4">
                  <div className="flex flex-wrap gap-2">
                    {/* Set-level danger zone — always available */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => setConfirmDeleteSet(true)}
                      disabled={isMutating}
                    >
                      <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                      Delete evaluator
                    </Button>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {isDraft && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setConfirmDeleteDraft(true)}
                          disabled={isMutating}
                        >
                          Delete draft
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSaveDraft(false)}
                          disabled={isMutating || !dirty}
                        >
                          {updateMutation.isPending ? (
                            <span className="inline-flex items-center gap-1.5">
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              Saving&hellip;
                            </span>
                          ) : (
                            'Save changes'
                          )}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            const validationError = validate();
                            if (validationError) {
                              setErrorMessage(validationError);
                              return;
                            }
                            setErrorMessage(null);
                            setConfirmActivate(true);
                          }}
                          disabled={isMutating}
                        >
                          {updateMutation.isPending || publishMutation.isPending ? (
                            <span className="inline-flex items-center gap-1.5">
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              Saving&hellip;
                            </span>
                          ) : (
                            'Save & activate'
                          )}
                        </Button>
                      </>
                    )}

                    {isPublished && (
                      existingDraft ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleOpenExistingDraft}
                          disabled={isMutating}
                        >
                          Open existing draft
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => setConfirmEditFromActive(true)}
                          disabled={isMutating}
                        >
                          {createVersionMutation.isPending ? (
                            <span className="inline-flex items-center gap-1.5">
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              Creating draft&hellip;
                            </span>
                          ) : (
                            'Edit (creates new draft)'
                          )}
                        </Button>
                      )
                    )}

                    {(isPublished || isArchived) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onOpenChange(false)}
                        disabled={isMutating}
                      >
                        Close
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmDiscard}
        onOpenChange={(next) => {
          if (!next) setConfirmDiscard(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              Your edits to this draft will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleDiscardConfirm}>Discard</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={confirmDeleteDraft}
        onOpenChange={(next) => {
          if (!next) setConfirmDeleteDraft(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this draft?</AlertDialogTitle>
            <AlertDialogDescription>
              The draft and all of its edits will be permanently deleted.
              {selectedVersion ? ` (Draft v${selectedVersion.version_number})` : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeleteDraft}
            >
              Delete draft
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={confirmDeleteSet}
        onOpenChange={(next) => {
          if (!next) setConfirmDeleteSet(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this evaluator?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold">{set?.name}</span> and all of its versions
              will be permanently deleted. Conversations will no longer be scored against it.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeleteSet}
            >
              Delete evaluator
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={confirmEditFromActive}
        onOpenChange={(next) => {
          if (!next) setConfirmEditFromActive(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Create a draft to edit?</AlertDialogTitle>
            <AlertDialogDescription>
              Editing creates a new draft based on the active version. The live evaluator
              keeps running until you activate the draft.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleEditFromActive}>
              Create draft
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={confirmActivate}
        onOpenChange={(next) => {
          if (!next && !isMutating) setConfirmActivate(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Save and activate this draft?</AlertDialogTitle>
            <AlertDialogDescription>
              Saving and activating this draft will make it the live evaluator
              {inSetActiveVersion ? (
                <>
                  , replacing v{inSetActiveVersion.version_number} of this evaluator
                </>
              ) : null}
              {otherActiveSetName ? (
                <>
                  {inSetActiveVersion ? ', and ' : ' and '}
                  archiving the currently active{' '}
                  <span className="font-semibold">{otherActiveSetName}</span>
                </>
              ) : null}
              . New conversations will be scored against it from now on.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isMutating}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmActivate(false);
                void handleSaveDraft(true);
              }}
            >
              Save & activate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default CustomEvalDetailDialog;
